/**
 * The "Show EDF annotations" toggle as one per-user preference (#976).
 *
 * The toggle used to live in four places with four independent states — the
 * Segment Selector, the EEG Preview, the Analysis TIME SERIES tab (saved per
 * analysis on the server) and the METRICS/SAM panel — all defaulting to ON, so
 * a clinician opening a recording was shown every device annotation and had to
 * switch it off in each surface. It is now a single value, defaulting to OFF,
 * that every surface reads and writes.
 *
 * Three layers, each covering a different distance:
 *
 *  - **Storage** — versioned, user-scoped key (`createPreference`), so the
 *    choice survives reloads and follows the clinician from one recording or
 *    analysis to the next.
 *  - **`showEdfAnnotations$`** — the live value for everything in this JS realm:
 *    every surface mounted in this tab, and the detached viewer windows, whose
 *    DOM is portalled out of this same Preact tree (`popout_frame.tsx`).
 *  - **BroadcastChannel** — other tabs and windows of the browser (the EEG
 *    Preview and an analysis are different routes, often open side by side).
 *
 * "Show triggers" is deliberately NOT part of this: it stays one instance per
 * view, default ON, not persisted (`trigger-annotations` spec).
 */

import { createPreference } from '@brainfocus/web/shared/storage/preference';
import { usePreference } from '@brainfocus/web/shared/storage/use_preference';

const preference = createPreference<boolean>('show-edf-annotations', {
    version: 1,
    defaultValue: () => false,
    parse: raw => (typeof raw === 'boolean' ? raw : undefined),
    userScope: 'optional',
    crossTab: true,
});

/**
 * The stored value, or `false` when there is none, it is unreadable, or storage
 * is unavailable. Never throws. `userId` defaults to the signed-in user (pass
 * `null` to force the unscoped key).
 */
export function loadShowEdfAnnotations(storage?: Storage, userId?: number | null): boolean {
    return preference.load({ storage, userId });
}

/**
 * Persist the value. Best effort: quota or access errors are swallowed.
 * Storage-injectable and side-effect free for tests — the app calls
 * {@link setShowEdfAnnotations}.
 */
export function saveShowEdfAnnotations(value: boolean, storage?: Storage, userId?: number | null): void {
    preference.save(value, { storage, userId });
}

/** Live value every surface reads. Seeded once from storage on first use. */
export const showEdfAnnotations$ = preference.value$;

/**
 * Open the cross-tab channel. Called once at bootstrap (`index.tsx`), never at
 * module load: under jsdom the bare `BroadcastChannel` is Node's, which keeps
 * the event loop alive and would hang Mocha. Idempotent.
 */
export function startShowEdfAnnotationsSync(): void {
    preference.startSync();
}

/** Change the preference everywhere: storage, this realm, and the other tabs. */
export function setShowEdfAnnotations(value: boolean): void {
    preference.set(value);
}

/** `[value, setter]` for components, re-rendering on every change. */
export function useShowEdfAnnotations(): [boolean, (value: boolean) => void] {
    return usePreference(preference);
}
